import type {ComponentType} from '@bitstillery/mithril'
import type {CmsLocaleTranslationBundle} from '@bitstillery/common/api/fact2server'
import {api, i18next, logger} from '@bitstillery/common/app'
import {cached} from '@bitstillery/common/lib/api_cache'
import {is_nested_object, property_of} from '@bitstillery/common/lib/utils'

import {$s} from '@/app'
import {PAGE_NOT_FOUND} from '@/cms/lib/not_found'
import {compile_cms_module} from '@/cms/runtime/compile'
import type {StatePortal} from '@/lib/state'

/**
 * Loading a published CMS page: its compiled module, and its copy.
 *
 * Everything here is deliberately blind to what is on screen — nothing touches `$s.page`, the rendered
 * tree, or (for the copy) the live i18next bundle. That is what lets one implementation serve all three
 * callers: `CmsDynamicPage` navigating, `runtime/prefetch` warming a page ahead of a click, and the SSR
 * hydration seeding what the server already rendered. Deciding *when* a loaded page becomes the visible
 * one belongs to the component (`commit_page`), and is the whole reason this file has no opinion on it.
 */

export const MISSING_EXPORT_ERROR = 'CMS module has no default or component export (e.g. add export default or name it CmsView)'

/** What a loaded page writes into `$s.page`; the header state stays the shell's own. */
export type CmsPageMeta = Omit<StatePortal['page'], 'header'>

/** A loaded CMS page: the compiled module, and what `$s.page` becomes once it is shown. */
export interface LoadedCmsPage {
    /** The compiled page; `null` when the path 404'd or the module failed to compile. */
    component: ComponentType | null
    /** The `$s.page` payload — carries the 404 marker or the compile error when there is no component. */
    page: CmsPageMeta
}

/** What `$s.page` becomes for a path that resolves to no published page. */
export function not_found_page(page_path: string): CmsPageMeta {
    return {compiled_js: null, error: PAGE_NOT_FOUND, hero_background: '', hero_mode: 'full', path: page_path}
}

async function fetch_and_compile(page_path: string): Promise<LoadedCmsPage> {
    const response = await api.get('portal/cms/page', {path: page_path})
    if (response.status_code === 404) {
        return {component: null, page: not_found_page(page_path)}
    }
    if (!response.success) {
        const error = `Could not load the page (${response.status_code})`
        return {component: null, page: {compiled_js: null, error, hero_background: '', hero_mode: 'full', path: page_path}}
    }
    const data = response.result

    let component: ComponentType | undefined
    let error: string | null = null
    try {
        component = await compile_cms_module(data.compiled_js)
        if (!component) {
            error = MISSING_EXPORT_ERROR
        }
    } catch (e) {
        error = e instanceof Error ? e.message : String(e)
    }

    return {
        component: component ?? null,
        page: {
            compiled_js: data.compiled_js,
            error,
            hero_background: data.hero_background,
            hero_mode: data.hero_mode,
            path: data.path,
        },
    }
}

/**
 * Fetch and compile a page. Concurrent callers share one request (a click landing on top of the prefetch
 * it triggered joins it), and the result is reused across navigations for as long as the ttl — which is
 * also how a publish reaches an open tab, since a compiled module can only go stale, never revalidate.
 *
 * Scoped per account, not per user: a published page is the same for everyone on the host, and the SSR
 * process may serve more than one. Failures and 404s are not stored, so the next visit tries again.
 */
export const load_page = cached('cms-page', fetch_and_compile, {
    scope: 'account',
    // The compiled component is a function; it cannot cross the SSR boundary as JSON.
    handoff: false,
    store_if: (loaded: LoadedCmsPage) => loaded.component !== null,
    ttl: 300_000,
})

/**
 * Seed the page the server rendered, whose module is already in `$s.page` — the client compiles it once
 * at boot rather than fetching it back, and `CmsDynamicPage` then finds it the way it finds any other
 * cached page. Returns the component, or `undefined` when the module has no usable export.
 */
export async function prime_ssr_cms_page(page_path: string): Promise<ComponentType | undefined> {
    if (!$s.page.compiled_js) {
        return undefined
    }
    const component = await compile_cms_module($s.page.compiled_js)
    if (!component) {
        return undefined
    }
    load_page.prime([page_path], {
        component,
        page: {
            compiled_js: $s.page.compiled_js,
            error: null,
            hero_background: $s.page.hero_background,
            hero_mode: $s.page.hero_mode,
            path: $s.page.path,
        },
    })
    return component
}

/**
 * The path of the page the server rendered, until the client has claimed it. `runtime/prefetch` asks
 * once, to recognise the hydration of that page — already on screen, already warm from the SSR handoff —
 * and leave it alone rather than stage it.
 */
let hydrated_cms_path = ''

/** True (once) for the page this tab hydrated, so its first navigation is not mistaken for one. */
export function claim_hydrated_cms_page(page_path: string): boolean {
    if (hydrated_cms_path !== page_path) {
        return false
    }
    hydrated_cms_path = ''
    return true
}

/**
 * A page's copy comes in two halves, and they reach the i18next bundle at different moments.
 *
 * *Page copy* is everything the page's own sections read. Those keys belong to this page alone, so they
 * are safe to add early — a page warmed ahead of a click (`runtime/prefetch`) needs them to render staged.
 *
 * *Hero copy* is `page.title` and `page.description`. Every page uses those same two keys, so the bundle
 * only ever holds one page's worth: whichever page wrote them last captions the hero and the breadcrumb.
 * They describe the page on screen, so they are held back here until `commit_page` shows the page they
 * belong to — and written again on every visit, since any page since could have taken the keys over.
 */
interface HeroCopy {
    description: string
    title: string
}

/**
 * Pages whose copy is in the bundle, each holding the hero copy its commit will show. Keyed by
 * `` `${language}|${path}` ``, since bundles are per language and the page cache is not.
 */
const added_pages = new Map<string, HeroCopy>()

/** The page whose hero copy is in the bundle right now, same key — '' until the first page commits. */
let hero_page = ''

/** A copy key of a page's `page` namespace; '' when the page has none. */
function page_copy_text(page: unknown, key: 'description' | 'title'): string {
    const text = property_of(page, key)
    return typeof text === 'string' ? text : ''
}

/** Record the SSR page's copy as added: `init_language` loaded it, hero copy and all, before this ran. */
export function mark_page_copy_loaded(language: string, page_path: string): void {
    const key = `${language}|${page_path}`
    const bundled: unknown = i18next.getResource(language, 'translation', 'page')
    added_pages.set(key, {description: page_copy_text(bundled, 'description'), title: page_copy_text(bundled, 'title')})
    hero_page = key
    hydrated_cms_path = page_path
}

/**
 * A page's copy, fetched but deliberately not in the bundle yet — fetching and adding are separate steps
 * here, so a page can be readied ahead of a click without its hero copy landing on the page still on
 * screen.
 *
 * Same endpoint, ttl and scope as `load_i18n_resources` (common/app.ts), which fetches the global half of
 * the very same copy — this one asks for a single page's keys (`page_only`).
 */
const page_copy = cached(
    'cms-page-copy',
    async (language: string, page_path: string): Promise<CmsLocaleTranslationBundle['translation'] | null> => {
        const {result, success} = await api.get('portal/cms/i18n', {
            language_code: language,
            page_only: true,
            path: page_path,
        })
        if (!success) {
            return null
        }
        // A page may have no copy in `language`.
        return result.resources[language]?.translation ?? null
    },
    {handoff: false, key: (language: string, page_path: string) => `${language}|${page_path}`, scope: 'public', ttl: 60_000},
)

/**
 * Add a page's copy to the bundle — everything it holds but the two hero keys, which are kept for
 * `show_hero_copy`. Returns `false` when the copy is not in hand yet, so the caller knows it has to await
 * `ensure_page_copy` first.
 */
export function add_page_copy(language: string, page_path: string): boolean {
    const key = `${language}|${page_path}`
    if (added_pages.has(key)) {
        return true
    }
    const translation = page_copy.peek(language, page_path)
    if (translation === undefined) {
        return false
    }
    const page = is_nested_object(translation?.page) ? translation.page : {}
    if (translation) {
        logger.info(`adding i18n resources for path: ${page_path}`)
        const {description: _description, title: _title, ...rest} = page
        i18next.addResourceBundle(language, 'translation', {...translation, page: rest}, true, true)
    }
    // Remembered even for a page with no copy of its own: a blank hero is what it wants shown, and
    // revisits then need no refetch.
    added_pages.set(key, {description: page_copy_text(page, 'description'), title: page_copy_text(page, 'title')})
    return true
}

/**
 * Ensure a page's copy is in the bundle, fetching it once if not. Safe on every render path (including
 * cache hits) — a no-op once it is there. The active language is captured up front so a language switch
 * mid-flight can't add the bundle under, or index it by, the wrong language (which would silently render
 * raw keys).
 */
export async function ensure_page_copy(page_path: string): Promise<void> {
    const language = $s.language
    if (add_page_copy(language, page_path)) {
        return
    }
    await page_copy(language, page_path)
    add_page_copy(language, page_path)
}

/**
 * Hand the hero and the breadcrumb to `page_path` — its title and description become what the two shared
 * keys resolve to. Called from `commit_page`, so the caption changes in the same step as the page under
 * it, and on every visit rather than the first: any page since could have taken the keys over.
 *
 * A page whose copy is not in hand keeps whatever the bundle holds. That is the server render, where the
 * bundle was loaded for this page to begin with; in the browser `commit_page` is only ever reached with
 * the copy added.
 */
export function show_hero_copy(page_path: string): void {
    const language = $s.language
    const key = `${language}|${page_path}`
    const hero = added_pages.get(key)
    if (!hero || hero_page === key) {
        return
    }
    hero_page = key
    i18next.addResourceBundle(language, 'translation', {page: {...hero}}, true, true)
}
